'use client';

import { useState } from 'react';
import Link from '@/components/Link';
import { cltw } from 'ui/utils';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from 'ui/components/NavigationMenu/NavigationMenu';
import { StaticIcon } from 'ui/components/Icon/Icon';
import { NavigationMenuListItem } from '../NavigationMenuListItem/NavigationMenuListItem';
import type { NavigationTree } from './getShopNavigation';
import { TriangleDown } from 'ui/icons/TriangleDown';

interface INavigation {
  navigationTree: NavigationTree[];
}

export const Navigation = ({ navigationTree }: INavigation) => {
  // Manually control NavigationMenu to ensure proper keyboard navigation.
  // Problem: On keyboard navigation, 'Enter' would trigger both link
  // navigation and menu expansion.
  // Solution: Make NavigationMenu semi-controlled.
  // - The parent trigger allows the native component to handle openening on
  //   menu hover.
  // - A second trigger is used for keyboard navigation, only visible on
  //   focus.
  // - The link is used only for navigation.

  return (
    <NavigationMenu
      className='static hidden px-3 xl:block'
      viewportClassName='mt-0'
      viewportWrapperClassName='top-[var(--header-height)]'
    >
      {/* To debug with the menu open, add prop value="<NavigationTree.label>" to NavigationMenu */}
      <NavigationMenuList>
        {navigationTree.map((root) => (
          <NavigationMenuItem key={root.label} value={root.label}>
            {root.children && root.children.length > 0 ? (
              <NavigationMenuTrigger asChild className='relative text-white'>
                <div>
                  {root.link ? (
                    <Link href={root.link}>{root.label}</Link>
                  ) : (
                    root.label
                  )}
                  {/* TODO: Add back keyboard navigation trigger but don't use NavigationMenuTrigger, just a button intead */}
                  {/* {root.children ? (
                    <NavigationMenuTrigger
                      aria-label={`Expand ${root.label} menu`}
                      className='absolute right-[-13px] p-0 pl-[7px] pt-[7px] opacity-0 focus-visible:opacity-100'
                      onClick={() => handleFocusTriggerClick(root)}
                    >
                      <StaticIcon alt='expand' color='#ffffff' size='small' IconComponent={TriangleDown} />
                    </NavigationMenuTrigger>
                  ) : null} */}
                </div>
              </NavigationMenuTrigger>
            ) : (
              <div className='relative inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-bold uppercase text-white'>
                {root.link ? (
                  <Link href={root.link}>{root.label}</Link>
                ) : (
                  root.label
                )}
              </div>
            )}
            {root.children && root.children.length > 0 ? (
              <NavigationMenuContent
                aria-label={`${root.label} menu`}
                className='mt-0'
              >
                <ul className={cltw('flex w-screen gap-3 p-4')}>
                  {root.children.map((node) => (
                    <NavigationMenuListItem
                      className='[&>span]:font-bold [&>span]:uppercase'
                      href={node.link}
                      key={node.label}
                      title={node.label}
                    >
                      <ul>
                        {node.children?.map((leaf) => (
                          <NavigationMenuListItem
                            className=''
                            href={leaf.link}
                            key={leaf.link}
                            title={leaf.label}
                          />
                        ))}
                      </ul>
                    </NavigationMenuListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            ) : null}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default Navigation;
